import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useLocale, useT } from '../i18n';
import { hubHref, hubKey, hubKindLabel, type HubHit, type SearchMeta } from '../lib/useEventSearch';
import { usePrice } from '../lib/currency';
import { distanceKm, type Geo } from '../lib/personal';
import { eventPath, slugify } from '../data/events';
import { TRIBUTE } from '../../shared/search-helpers.mjs';
import type { Event } from '../types';
import { HubAvatar } from './Poster';

/**
 * Pieces of the search answer that the search page, the search overlay and the homepage's
 * search box all show the same way (functions/api/search.ts says what they mean).
 */

const priced = (e: Pick<Event, 'lowestPrice'>) => typeof e.lowestPrice === 'number' && e.lowestPrice > 0;

/**
 * A suggestion's price with its fee basis — "from $185 all-in" or "from $63 + fees", as the
 * build delivered it (lowestPrice and lowestAllIn already follow shared/providers.mjs
 * headlineOffer, suspect prices excluded). Unpriced: "not listed yet" for an announced event
 * no partner lists, else "compare" — never "on sale soon", which nobody has told us.
 */
export const SuggestPrice = ({ event, soon }: { event: Event; soon: boolean }) => {
  const t = useT();
  const price = usePrice();
  if (priced(event))
    return (
      <span className="tabular shrink-0 whitespace-nowrap text-right text-[12.5px] leading-tight">
        <span className="font-semibold text-foreground">{t('from {price}', { price: price(event.lowestPrice!) })}</span>{' '}
        <span className="text-muted-foreground">{event.lowestAllIn ? t('all-in') : t('+ fees')}</span>
      </span>
    );
  return <span className="shrink-0 whitespace-nowrap text-[12.5px] text-muted-foreground">{soon ? t('not listed yet') : t('compare')}</span>;
};

/** Is this event one of the page's own (for the page's price and nearest city)? */
const onPage = (h: HubHit, e: Event) => {
  if (h.kind === 'artist') return !!e.artist && slugify(e.artist) === h.slug;
  if (h.kind === 'venue') return slugify(e.venue) === h.slug;
  if (h.kind === 'city') return slugify(e.city) === h.slug;
  if (h.kind === 'team')
    return (e.teams ?? []).some((name) => {
      const s = slugify(name.replace(/\s*\([^)]*\)\s*/g, ' '));
      return s === h.slug || `${slugify(e.city)}-${s}` === h.slug || h.slug.endsWith(`-${s}`);
    });
  return false;
};

/**
 * A list page's "from" price, only when its fee basis is known: one of the returned events of
 * that page carries exactly the page's lowest price (hub_stat.min mixes both bases — the Knicks'
 * $17 is an all-in Pelicans game). Otherwise no price at all.
 */
export const hubPrice = (h: HubHit, events: Event[]): { price: number; allIn: boolean } | null => {
  if (!h.min || h.min <= 0) return null;
  const e = events.find((x) => x.lowestPrice === h.min && onPage(h, x));
  return e ? { price: h.min, allIn: !!e.lowestAllIn } : null;
};

/** The city of an act's or club's date nearest the visitor — only when it is much nearer than
 *  where the next date is (Metallica's next nights are in Las Vegas, one is a drive from New
 *  York); null without a location, or when the next date is close anyway. */
export const nearestCity = (h: HubHit, events: Event[], geo: Geo | null): string | null => {
  if ((h.kind !== 'artist' && h.kind !== 'team') || !geo?.lat || !geo?.lon) return null;
  const km = (e: Event) => (!e.lat && !e.lng ? Infinity : distanceKm(geo.lat!, geo.lon!, e.lat, e.lng));
  const mine = events.filter((e) => onPage(h, e));
  let best: Event | null = null;
  for (const e of mine) if (km(e) < (best ? km(best) : Infinity)) best = e;
  const next = [...mine].sort((a, b) => a.date.localeCompare(b.date))[0];
  if (!best || !next || best.city === next.city) return null;
  const far = km(next);
  return far > 100 && km(best) < far / 2 ? best.city : null;
};

/** Rows that need their time shown: the same act has another row that day (a matinee and an
 *  evening show look identical otherwise). */
export const sameDayIds = (events: Event[]): Set<string> => {
  const seen = new Map<string, string[]>();
  for (const e of events) {
    const k = `${(e.artist ?? e.name).toLowerCase()}|${e.date}`;
    seen.set(k, [...(seen.get(k) ?? []), e.id]);
  }
  return new Set([...seen.values()].filter((ids) => ids.length > 1).flat());
};

/** Suggestions: the real act before tribute and cover nights (order kept otherwise). */
export const realActsFirst = (events: Event[]): Event[] => {
  const tribute = (e: Event) => TRIBUTE.test(`${e.name} ${e.artist ?? ''}`);
  return [...events.filter((e) => !tribute(e)), ...events.filter(tribute)];
};

/** One row of the search box's list: a list page or a show, with where it leads. */
export type BoxItem = { href: string } & ({ hub: HubHit; event?: undefined } | { event: Event; hub?: undefined });

/**
 * The search box's rows in the order a fan reads them: the real pages and their dates first,
 * tribute and cover acts' pages and nights after ("metallica": Metallica's page and its Sphere
 * nights, then The Four Horsemen — whose page came second, above Metallica's own dates).
 * `rows`: the shows to list, at most as many as the box shows.
 */
export const boxRows = (pages: HubHit[], rows: Event[]): BoxItem[] => {
  const tributePage = (h: HubHit) => h.kind === 'artist' && TRIBUTE.test(h.name);
  const tributeRow = (e: Event) => TRIBUTE.test(`${e.name} ${e.artist ?? ''}`);
  const page = (hub: HubHit): BoxItem => ({ hub, href: hubHref(hub) });
  const show = (event: Event): BoxItem => ({ event, href: eventPath(event) });
  return [
    ...pages.filter((h) => !tributePage(h)).map(page),
    ...rows.filter((e) => !tributeRow(e)).map(show),
    ...pages.filter(tributePage).map(page),
    ...rows.filter(tributeRow).map(show),
  ];
};

/** A list page's line in the search box: kind and label, dates, next date or nearest city,
 *  and its price when the fee basis is known. */
export const useHubMeta = () => {
  const t = useT();
  const tag = useHubTag();
  const price = usePrice();
  return (h: HubHit, events: Event[], geo: Geo | null, fmtDate: (d: string) => string) => {
    const p = hubPrice(h, events);
    const near = nearestCity(h, events, geo);
    return [
      [hubKindLabel(t, h.kind), tag(h)].filter(Boolean).join(' · '),
      // (a tournament's card counts no dates: its page holds them)
      h.kind === 'tour' && !h.n ? '' : t('{n} date|{n} dates', { n: h.n }),
      near ? t('nearest: {city}', { city: near }) : h.next ? t('next {date}', { date: fmtDate(h.next) }) : '',
      p ? `${t('from {price}', { price: price(p.price) })} ${p.allIn ? t('all-in') : t('+ fees')}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
  };
};

/** /search?q=… — `exact` turns spelling correction off ("Search instead for kniks"). */
export const searchHref = (q: string, exact = false) => `/search?q=${encodeURIComponent(q)}${exact ? '&exact=1' : ''}`;

/** What tells two same-name pages apart: "NFL", "Concerts · Sphere", "Boston". */
export const useHubTag = () => {
  const t = useT();
  return (h: HubHit) => [h.label?.tag ? t(h.label.tag) : '', h.label?.where ?? ''].filter(Boolean).join(' · ');
};

/** A holiday word's name in the reader's language (literal t() calls, so the dictionaries check them). */
const holidayName = (h: string | undefined, t: (k: string) => string) =>
  h === 'christmas'
    ? t('Christmas')
    : h === 'thanksgiving'
      ? t('Thanksgiving')
      : h === 'new-years-eve'
        ? t("New Year's Eve")
        : h === 'new-years-day'
          ? t("New Year's Day")
          : h === 'halloween'
            ? t('Halloween')
            : h === 'independence-day'
              ? t('Independence Day')
              : h === 'memorial-day'
                ? t('Memorial Day')
                : h === 'labor-day'
                  ? t('Labor Day')
                  : h === 'valentines'
                    ? t("Valentine's Day")
                    : '';

/** "December 2026" in the reader's language. */
const monthName = (ym: string, locale: string) => new Date(`${ym}-15T12:00:00`).toLocaleDateString(locale, { month: 'long', year: 'numeric' });

/**
 * One line per thing the answer says besides its rows: the spelling it searched instead (with a
 * way back to what was typed), a known act with no dates, tribute shows only, a partial match,
 * and how an intent query was read ("Comedy · This weekend · Near you") with the page that
 * holds the whole list. Never a claim the data cannot back: "no dates are listed on TixInfo",
 * never "not touring". `onResult`: that page is followed (a tapped result, for the search counter).
 */
export const SearchNotice = ({
  meta,
  query,
  onNavigate,
  onResult,
  className = '',
}: {
  meta: SearchMeta;
  query: string;
  onNavigate?: () => void;
  onResult?: () => void;
  className?: string;
}) => {
  const lines = useNoticeLines(meta, query, onNavigate, onResult);
  if (!lines.length) return null;
  return <NoteLines lines={lines} className={className} />;
};

/** The notice's lines, one block per sentence: text that lands after the answer stays smaller
 *  than the heading (a two-line block became the phone's Largest Contentful Paint at ~6 s). */
const NoteLines = ({ lines, className }: { lines: ReactNode[]; className: string }) => (
  <div className={`space-y-1 text-[14px] leading-snug text-muted-foreground ${className}`} role="status">
    {lines.map((l, i) => (
      <p key={i}>{l}</p>
    ))}
  </div>
);

/** SearchNotice's lines (none: the answer says nothing besides its rows). `onResult`: the
 *  reading's page is followed (a tapped result, for the search counter). */
const useNoticeLines = (meta: SearchMeta, query: string, onNavigate?: () => void, onResult?: () => void): ReactNode[] => {
  const t = useT();
  const locale = useLocale();
  const lines: ReactNode[] = [];
  if (meta.didYouMean && meta.original) {
    lines.push(
      <span key="dym">
        {t('Showing results for')} <strong className="font-semibold text-foreground">{meta.didYouMean}</strong>
        {' · '}
        <Link to={searchHref(meta.original, true)} onClick={onNavigate} className="font-medium text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary">
          {t('Search instead for {q}', { q: `“${meta.original}”` })}
        </Link>
      </span>
    );
  }
  const name = meta.noticeName ?? query;
  // one block per sentence (NoteLines)
  if (meta.notice === 'tribute' || (meta.notice === 'partial' && meta.tribute)) {
    lines.push(
      <span key="n">
        <span className="block">{t('No {name} dates are listed on TixInfo right now.', { name })}</span>
        <span className="block">{t('These are tribute and cover shows.')}</span>
      </span>
    );
  }
  if (meta.notice === 'help')
    lines.push(
      <span key="help">
        {t('We compare prices. The marketplace you bought from handles your order and refunds.')}{' '}
        <Link to={/cancel|resched|postpon|refund|money back/i.test(query) ? '/faq#canceled' : '/faq#support'} onClick={onNavigate} className="whitespace-nowrap font-medium text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary">
          {t('Read the FAQ →')}
        </Link>
      </span>
    );
  if (meta.notice === 'elsewhere') {
    // "No Amsterdam date is listed. Next dates for Harry Styles:" (the reading below names the act)
    lines.push(
      <span key="n">
        {name ? <span className="block">{t('No {place} date is listed.', { place: name })}</span> : null}
        <span className="block">{t('Next dates for {name}:', { name: meta.reading?.entity?.name ?? '' })}</span>
      </span>
    );
  }
  if (meta.notice === 'no-dates') lines.push(<span key="n">{t('No {name} dates are listed on TixInfo right now.', { name })}</span>);
  if (meta.notice === 'partial' && meta.dropped) lines.push(<span key="n">{t('No listing matches every word. These match without “{word}”.', { word: meta.dropped })}</span>);
  if (meta.notice === 'postseason') lines.push(<span key="n">{t('No {round} games are listed on TixInfo right now. These are the World Series games.', { round: name })}</span>);
  const r = meta.reading;
  if (r) {
    const cat = r.cat === 'music' ? t('Concerts') : r.cat === 'comedy' ? t('Comedy') : r.cat === 'sports' ? t('Sports') : r.cat === 'theater' ? t('Theater') : '';
    const when =
      r.when === 'tonight'
        ? t('Tonight')
        : r.when === 'today'
          ? t('Today')
          : r.when === 'tomorrow'
            ? t('Tomorrow')
            : r.when === 'weekend'
              ? t('This weekend')
              : r.when === 'next-weekend'
                ? t('Next weekend')
                : r.when === 'week'
                  ? t('This week')
                  : r.when === 'next-week'
                    ? t('Next week')
                    : r.when === 'opener'
                      ? t('Opening night')
                      : r.when === 'weekday' && r.day
                        ? new Date(`${r.day}T12:00:00`).toLocaleDateString(locale, { weekday: 'long', month: 'short', day: 'numeric' })
                        : r.when === 'holiday'
                        ? holidayName(r.holiday, t)
                        : r.when === 'month' && r.month
                          ? monthName(r.month, locale)
                          : '';
    const sport = r.sport === 'soccer' ? t('Soccer') : '';
    const parts = [r.entity?.name, r.genre?.name, cat, sport, r.place?.name, when, r.near ? t('Near you') : ''].filter(Boolean);
    lines.push(
      <span key="reading">
        <strong className="font-semibold text-foreground">{parts.join(' · ')}</strong>
        {meta.route && (
          <>
            {' · '}
            <Link
              to={meta.route.href}
              onClick={() => {
                onResult?.();
                onNavigate?.();
              }}
              className="font-medium text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary"
            >
              {t('See the full list →')}
            </Link>
          </>
        )}
      </span>
    );
  }
  return lines;
};

/** Does the answer say something besides its rows (SearchNotice has a line for it)? */
const hasNotice = (meta: SearchMeta) => !!(meta.didYouMean || (meta.notice && meta.notice !== 'none') || meta.reading);

/** Has the search box something to say above its options: a notice line, or nothing found? */
export const hasBoxNote = (meta: SearchMeta) => hasNotice(meta) || meta.notice === 'none';

/**
 * The lines above the search box's options (the homepage box and the overlay): the answer's
 * notice — and when nothing is on the bill, that first (SearchNotice has no line for "none"; the
 * search page says it in its own words below the box). Under it the way forward: the reading's
 * page ("metallica nyc": Metallica has no New York date — the box showed only "Metallica · New
 * York · See the full list →", never that nothing was found), else the A–Z list of every artist.
 */
export const BoxNote = ({
  meta,
  query,
  onNavigate,
  onResult,
  className = '',
}: {
  meta: SearchMeta;
  query: string;
  onNavigate?: () => void;
  onResult?: () => void;
  className?: string;
}) => {
  const t = useT();
  const lines = useNoticeLines(meta, query, onNavigate, onResult);
  if (meta.notice === 'none') {
    if (!lines.length)
      lines.push(
        <Link to="/artists" onClick={onNavigate} className="font-medium text-primary underline decoration-primary/30 underline-offset-4 hover:decoration-primary">
          {t('Browse all artists A–Z')}
        </Link>
      );
    lines.unshift(
      <>
        {t('Nothing on the bill for')} “{query}”
      </>
    );
  }
  if (!lines.length) return null;
  return <NoteLines lines={lines} className={className} />;
};

/**
 * Two or three pages a query names equally ("kings": the LA Kings and the Sacramento Kings),
 * side by side with what tells them apart — no one of them called the best match. `onPick`: a
 * chip is followed (a tapped result, for the search counter).
 */
export const WhichChips = ({
  hubs,
  which,
  onNavigate,
  onPick,
  className = '',
}: {
  hubs: HubHit[];
  which: string[];
  onNavigate?: () => void;
  onPick?: (h: HubHit) => void;
  className?: string;
}) => {
  const t = useT();
  const tag = useHubTag();
  const picks = which.map((k) => hubs.find((h) => hubKey(h) === k)).filter((h): h is HubHit => !!h);
  if (picks.length < 2) return null;
  return (
    <div className={className}>
      <p className="mb-2 text-[13px] font-medium text-muted-foreground">{t('Which one do you mean?')}</p>
      <div className="flex flex-wrap gap-2">
        {picks.map((h) => (
          <Link
            key={hubKey(h)}
            to={hubHref(h)}
            onClick={() => {
              onPick?.(h);
              onNavigate?.();
            }}
            className="group flex min-h-[48px] items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 transition-colors hover:border-foreground/30"
          >
            <HubAvatar name={h.name} src={h.image} size={36} round={h.kind === 'artist' || h.kind === 'team'} />
            <span className="min-w-0">
              <span className="block max-w-[15rem] truncate text-[15px] font-semibold group-hover:text-primary">{h.name}</span>
              <span className="block text-[12px] text-muted-foreground">{[hubKindLabel(t, h.kind), tag(h)].filter(Boolean).join(' · ')}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};
